import { useState } from "react";
import axios from "axios";
import { Play, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * NS-C1 (27 oktober 2026): een codeblok uit een antwoord draaien in de sandbox, na een bevestiging.
 *
 * Keuze K3, de veiligste werkende optie: ELKE rit vraagt een bevestiging. Het venster toont de
 * code die draait; de server leest haar uit het opgeslagen antwoord en draait niets als zij
 * afwijkt (`backend/services/coderit.py`). Geen netwerk, hooguit 30 seconden.
 */

/** Dezelfde taalnamen als de backend (`coderit.TALEN`). */
export const TALEN = { python: "python", py: "python", python3: "python", bash: "bash", sh: "bash", shell: "bash", zsh: "bash" };

const BLOK = /^```[ \t]*([\w+-]*)[ \t]*\n([\s\S]*?)\n```[ \t]*$/gm;

/** De codeblokken van een antwoord, zoals de backend ze telt. PUUR. */
export function blokken(tekst) {
  return [...(tekst || "").matchAll(BLOK)].map((m) => ({ taal: TALEN[m[1].toLowerCase()] || "", code: m[2] }));
}

/** Het nummer van dit blok in het antwoord, of -1 als het geen draaibaar blok is. PUUR. */
export function nummerVan(bron, code) {
  const i = blokken(bron).findIndex((b) => b.code === code);
  return i >= 0 && blokken(bron)[i].taal ? i : -1;
}

export default function CodeRit({ rit, code }) {
  const { t } = useTaal();
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [uit, setUit] = useState(null);
  const [fout, setFout] = useState("");
  const nummer = rit ? nummerVan(rit.bron, code) : -1;
  if (nummer < 0) return null;

  const draai = async () => {
    setBezig(true);
    setFout("");
    try {
      const { data } = await axios.post(
        `${API}/conversations/${rit.gesprekId}/messages/${rit.berichtId}/code`,
        { blok: nummer, code, bevestigd: true });
      setUit(data);
    } catch (e) {
      setFout(e?.response?.data?.detail || t("Draaien lukte niet."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <>
      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setUit(null); setFout(""); setOpen(true); }}
              data-testid="code-draai">
        <Play className="h-3.5 w-3.5 mr-1" aria-hidden="true" />{t("Draaien")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)]" data-testid="code-venster">
          <DialogHeader><DialogTitle>{t("Deze code draaien?")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("In de sandbox op deze Mac, zonder netwerk en hooguit 30 seconden. Er draait precies wat hieronder staat.")}
          </p>
          <pre className="max-h-60 overflow-auto rounded border bg-muted/40 p-2 text-xs" data-testid="code-getoond">{code}</pre>
          {!uit && (
            <Button onClick={draai} disabled={bezig} data-testid="code-bevestig">
              {bezig ? <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden="true" /> : <Play className="h-4 w-4 mr-1" aria-hidden="true" />}
              {t("Ja, draai in de sandbox")}
            </Button>
          )}
          {fout && <p className="text-sm text-destructive" role="alert" data-testid="code-fout">{fout}</p>}
          {uit && (
            <section className="space-y-1" data-testid="code-uitslag">
              <p className="text-xs text-muted-foreground" data-testid="code-afloop">
                {uit.timed_out ? t("Gestopt na 30 seconden.") : t("Klaar, afsluitcode {code}.", { code: String(uit.exit_code) })}
              </p>
              {uit.stdout && <pre className="max-h-60 overflow-auto rounded border p-2 text-xs" data-testid="code-stdout">{uit.stdout}</pre>}
              {uit.stderr && <pre className="max-h-40 overflow-auto rounded border p-2 text-xs text-destructive" data-testid="code-stderr">{uit.stderr}</pre>}
            </section>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
